import React, { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Download,
  EyeOff,
  PlusCircle,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

// ─── Test Data ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "active",   label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending",  label: "Pending" },
];

const CATEGORY_OPTIONS = [
  { value: "Electronics", label: "Electronics" },
  { value: "Clothing",    label: "Clothing" },
  { value: "Food",        label: "Food" },
  { value: "Beauty",      label: "Beauty" },
  { value: "Sports",      label: "Sports" },
  { value: "Home",        label: "Home" },
];

const TEST_DATA = [
  { id: 1,  sku: "SKU-001", name: "Wireless Bluetooth Headphones",      category: "Electronics", status: "active",   price: 2499, stock: 145, created_at: "2024-01-15" },
  { id: 2,  sku: "SKU-002", name: "Cotton T-Shirt Pack of 3",           category: "Clothing",    status: "active",   price: 599,  stock: 320, created_at: "2024-01-18" },
  { id: 3,  sku: "SKU-003", name: "Organic Green Tea 50 Bags",          category: "Food",        status: "active",   price: 299,  stock: 510, created_at: "2024-01-20" },
  { id: 4,  sku: "SKU-004", name: "Face Moisturiser SPF50",             category: "Beauty",      status: "pending",  price: 849,  stock: 88,  created_at: "2024-01-22" },
  { id: 5,  sku: "SKU-005", name: "Yoga Mat Anti-Slip 6mm",             category: "Sports",      status: "active",   price: 1199, stock: 67,  created_at: "2024-02-01" },
  { id: 6,  sku: "SKU-006", name: "Stainless Steel Water Bottle 1L",    category: "Home",        status: "active",   price: 449,  stock: 234, created_at: "2024-02-03" },
  { id: 7,  sku: "SKU-007", name: "USB-C Charging Cable 2m",            category: "Electronics", status: "active",   price: 349,  stock: 412, created_at: "2024-02-05" },
  { id: 8,  sku: "SKU-008", name: "Running Shorts Dry-Fit",             category: "Sports",      status: "inactive", price: 699,  stock: 0,   created_at: "2024-02-07" },
  { id: 9,  sku: "SKU-009", name: "Vitamin C Serum 30ml",               category: "Beauty",      status: "active",   price: 1299, stock: 156, created_at: "2024-02-10" },
  { id: 10, sku: "SKU-010", name: "Basmati Rice Premium 5kg",           category: "Food",        status: "active",   price: 499,  stock: 600, created_at: "2024-02-12" },
  { id: 11, sku: "SKU-011", name: "Noise Cancelling Earbuds TWS",       category: "Electronics", status: "pending",  price: 3499, stock: 42,  created_at: "2024-02-14" },
  { id: 12, sku: "SKU-012", name: "Linen Formal Shirt",                 category: "Clothing",    status: "active",   price: 1099, stock: 95,  created_at: "2024-02-16" },
  { id: 13, sku: "SKU-013", name: "Bamboo Cutting Board Set",           category: "Home",        status: "active",   price: 799,  stock: 178, created_at: "2024-02-18" },
  { id: 14, sku: "SKU-014", name: "Protein Whey Chocolate 1kg",         category: "Food",        status: "active",   price: 2199, stock: 84,  created_at: "2024-02-20" },
  { id: 15, sku: "SKU-015", name: "Resistance Bands Set of 5",          category: "Sports",      status: "inactive", price: 549,  stock: 0,   created_at: "2024-02-22" },
  { id: 16, sku: "SKU-016", name: "Smart LED Desk Lamp",                category: "Electronics", status: "active",   price: 1899, stock: 63,  created_at: "2024-03-01" },
  { id: 17, sku: "SKU-017", name: "Hair Growth Serum 50ml",             category: "Beauty",      status: "pending",  price: 999,  stock: 112, created_at: "2024-03-03" },
  { id: 18, sku: "SKU-018", name: "Jeans Slim Fit Dark Wash",           category: "Clothing",    status: "active",   price: 1499, stock: 207, created_at: "2024-03-05" },
  { id: 19, sku: "SKU-019", name: "Cast Iron Skillet 26cm",             category: "Home",        status: "active",   price: 2899, stock: 39,  created_at: "2024-03-07" },
  { id: 20, sku: "SKU-020", name: "Almond Butter Crunchy 400g",         category: "Food",        status: "active",   price: 649,  stock: 288, created_at: "2024-03-09" },
  { id: 21, sku: "SKU-021", name: "Portable Power Bank 20000mAh",       category: "Electronics", status: "active",   price: 2299, stock: 134, created_at: "2024-03-11" },
  { id: 22, sku: "SKU-022", name: "Sports Socks Pack of 6",             category: "Sports",      status: "active",   price: 399,  stock: 450, created_at: "2024-03-13" },
  { id: 23, sku: "SKU-023", name: "Argan Oil Treatment 100ml",          category: "Beauty",      status: "inactive", price: 1599, stock: 0,   created_at: "2024-03-15" },
  { id: 24, sku: "SKU-024", name: "Non-Stick Cookware Set 5pc",         category: "Home",        status: "active",   price: 3299, stock: 28,  created_at: "2024-03-17" },
  { id: 25, sku: "SKU-025", name: "Mixed Nuts Premium 500g",            category: "Food",        status: "active",   price: 799,  stock: 367, created_at: "2024-03-19" },
  { id: 26, sku: "SKU-026", name: "Mechanical Keyboard TKL",            category: "Electronics", status: "pending",  price: 4599, stock: 21,  created_at: "2024-03-21" },
  { id: 27, sku: "SKU-027", name: "Windcheater Jacket Waterproof",      category: "Clothing",    status: "active",   price: 2199, stock: 76,  created_at: "2024-03-23" },
  { id: 28, sku: "SKU-028", name: "Dumbbell Set Adjustable 20kg",       category: "Sports",      status: "active",   price: 5499, stock: 14,  created_at: "2024-03-25" },
  { id: 29, sku: "SKU-029", name: "Mattress Protector Waterproof King", category: "Home",        status: "active",   price: 1699, stock: 92,  created_at: "2024-03-27" },
  { id: 30, sku: "SKU-030", name: "Cold Press Juice Cleanser Set",      category: "Food",        status: "inactive", price: 1299, stock: 0,   created_at: "2024-03-29" },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:   "bg-green-100 text-green-700 border-green-200",
  inactive: "bg-red-100 text-red-700 border-red-200",
  pending:  "bg-yellow-100 text-yellow-700 border-yellow-200",
};

function StatusBadge({ status }) {
  return (
    <Badge className={cn("capitalize font-medium", STATUS_STYLES[status])}>
      {status}
    </Badge>
  );
}

// ─── DataTableColumnHeader ────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table-column-header.tsx

function DataTableColumnHeader({ column, label, className }) {
  if (!column.getCanSort() && !column.getCanHide()) {
    return <div className={cn("text-sm", className)}>{label}</div>;
  }

  const sorted = column.getIsSorted();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "-ml-3 h-8 data-[state=open]:bg-accent [&_svg]:size-3.5 [&_svg]:text-muted-foreground",
            className
          )}
        >
          {label}
          {sorted === "desc" ? (
            <ChevronDown />
          ) : sorted === "asc" ? (
            <ChevronUp />
          ) : (
            <ChevronsUpDown />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-32">
        {column.getCanSort() && (
          <>
            <DropdownMenuCheckboxItem
              checked={sorted === "asc"}
              onCheckedChange={() => column.toggleSorting(false)}
            >
              <ChevronUp /> Asc
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sorted === "desc"}
              onCheckedChange={() => column.toggleSorting(true)}
            >
              <ChevronDown /> Desc
            </DropdownMenuCheckboxItem>
            {sorted && (
              <DropdownMenuItem onClick={() => column.clearSorting()}>
                <X /> Reset
              </DropdownMenuItem>
            )}
          </>
        )}
        {column.getCanSort() && column.getCanHide() && <DropdownMenuSeparator />}
        {column.getCanHide() && (
          <DropdownMenuCheckboxItem
            checked={!column.getIsVisible()}
            onCheckedChange={() => column.toggleVisibility(false)}
          >
            <EyeOff /> Hide
          </DropdownMenuCheckboxItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── DataTableViewOptions ─────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table-view-options.tsx

function DataTableViewOptions({ table }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="ml-auto h-8">
          <SlidersHorizontal />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter((col) => col.getCanHide())
          .map((col) => (
            <DropdownMenuCheckboxItem
              key={col.id}
              className="capitalize"
              checked={col.getIsVisible()}
              onCheckedChange={(val) => col.toggleVisibility(!!val)}
            >
              {col.columnDef.meta?.label ?? col.id}
            </DropdownMenuCheckboxItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── FacetedFilter ────────────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table-faceted-filter.tsx

function FacetedFilter({ column, title, options }) {
  const selected = new Set(column?.getFilterValue() ?? []);

  function toggle(value) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    column.setFilterValue(next.size ? [...next] : undefined);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircle />
          {title}
          {selected.size > 0 && (
            <>
              <span className="mx-1 h-4 w-px bg-border" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selected.size}
              </Badge>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-44">
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={selected.has(opt.value)}
            onCheckedChange={() => toggle(opt.value)}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
        {selected.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="justify-center text-center text-muted-foreground"
              onClick={() => column.setFilterValue(undefined)}
            >
              Clear filter
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── DataTableToolbar ─────────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table-toolbar.tsx

function DataTableToolbar({ table }) {
  const isFiltered =
    table.getState().columnFilters.length > 0 ||
    !!table.getState().globalFilter;

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Input
          placeholder="Search name or SKU…"
          value={table.getState().globalFilter ?? ""}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          className="h-8 w-52"
        />
        <FacetedFilter
          column={table.getColumn("status")}
          title="Status"
          options={STATUS_OPTIONS}
        />
        <FacetedFilter
          column={table.getColumn("category")}
          title="Category"
          options={CATEGORY_OPTIONS}
        />
        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => {
              table.resetColumnFilters();
              table.setGlobalFilter("");
            }}
          >
            Reset
            <X />
          </Button>
        )}
      </div>
      <DataTableViewOptions table={table} />
    </div>
  );
}

// ─── DataTablePagination ──────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table-pagination.tsx

function DataTablePagination({ table }) {
  const selected = table.getFilteredSelectedRowModel().rows.length;
  const total    = table.getFilteredRowModel().rows.length;

  return (
    <div className="flex items-center justify-between flex-wrap gap-4 px-1">
      <p className="text-sm text-muted-foreground">
        {selected} of {total} row{total !== 1 ? "s" : ""} selected
      </p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">Rows per page</p>
          <select
            className="h-8 rounded-md border border-input bg-transparent px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <p className="text-sm font-medium whitespace-nowrap">
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </p>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────
// Mirrors tablecn/src/components/data-table/data-table.tsx

function DataTable({ table, actionBar, children }) {
  return (
    <div className="flex w-full flex-col gap-2.5">
                        {actionBar && table.getFilteredSelectedRowModel().rows.length > 0 && actionBar}
      {children}
      <div className="overflow-hidden rounded-md border">

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2.5">

        <DataTablePagination table={table} />
        
      </div>
    </div>
  );
}

// ─── Column Definitions ───────────────────────────────────────────────────────

function buildColumns() {
  return [
    {
      id: "select",
      meta: { label: "Select" },
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(val) => table.toggleAllPageRowsSelected(!!val)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(val) => row.toggleSelected(!!val)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "sku",
      meta: { label: "SKU" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="SKU" />,
      cell: ({ getValue }) => (
        <span className="font-mono text-xs font-medium text-muted-foreground">
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: "name",
      meta: { label: "Name" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Name" />,
      cell: ({ getValue }) => (
        <span className="font-medium max-w-[260px] truncate block">{getValue()}</span>
      ),
    },
    {
      accessorKey: "category",
      meta: { label: "Category" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Category" />,
      filterFn: (row, id, vals) => !vals?.length || vals.includes(row.getValue(id)),
    },
    {
      accessorKey: "status",
      meta: { label: "Status" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Status" />,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
      filterFn: (row, id, vals) => !vals?.length || vals.includes(row.getValue(id)),
    },
    {
      accessorKey: "price",
      meta: { label: "Price (₹)" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Price (₹)" />,
      cell: ({ getValue }) => (
        <span className="tabular-nums">₹{getValue().toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "stock",
      meta: { label: "Stock" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Stock" />,
      cell: ({ getValue }) => {
        const v = getValue();
        return (
          <span className={v === 0 ? "text-destructive font-medium" : "tabular-nums"}>
            {v === 0 ? "Out of stock" : v.toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      meta: { label: "Created" },
      header: ({ column }) => <DataTableColumnHeader column={column} label="Created" />,
      cell: ({ getValue }) => (
        <span className="text-muted-foreground text-xs whitespace-nowrap">
          {new Date(getValue()).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
  ];
}

// ─── TestTable ────────────────────────────────────────────────────────────────

export default function TestTable() {
  const [sorting, setSorting]                 = useState([]);
  const [columnFilters, setColumnFilters]     = useState([]);
  const [globalFilter, setGlobalFilter]       = useState("");
  const [rowSelection, setRowSelection]       = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  const columns = useMemo(() => buildColumns(), []);

  const table = useReactTable({
    data: TEST_DATA,
    columns,
    state: { sorting, columnFilters, globalFilter, rowSelection, columnVisibility },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _col, q) =>
      row.getValue("name").toLowerCase().includes(q.toLowerCase()) ||
      row.getValue("sku").toLowerCase().includes(q.toLowerCase()),
    initialState: { pagination: { pageSize: 10 } },
  });

  const selectedCount = table.getFilteredSelectedRowModel().rows.length;

  const actionBar = (
    <div className="flex items-center gap-2 rounded-md border bg-primary px-4 py-2 text-sm text-primary-foreground">
      <span className="font-medium">
        {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
      </span>
      <div className="flex-1" />
      <Button
        size="sm"
        variant="secondary"
        className="h-7 text-xs"
        onClick={() => alert(`Export ${selectedCount} rows`)}
      >
        <Download className="size-3" />
        Export
      </Button>
      <Button
        size="sm"
        className="h-7 text-xs bg-destructive text-destructive-foreground hover:bg-destructive/90"
        onClick={() => {
          table.resetRowSelection();
        }}
      >
        <Trash2 className="size-3" />
        Delete
      </Button>
    </div>
  );

  return (
    <div className="p-6 max-w-full font-sans">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">SKU Test Table</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage and filter your SKU inventory.
        </p>
      </div>
      <DataTable table={table} actionBar={actionBar}>
        <DataTableToolbar table={table} />
      </DataTable>
    </div>
  );
}
